import { useGmbGovernanceOverview } from '../../hooks/useGmbGovernance';
import TutorialCard from '../TutorialCard';
import { GBMContentWrapper } from './shared/GMBLoadingState';
import GBMStatCard from './shared/GBMStatCard';
import GMBSeverityBadge from './shared/GMBSeverityBadge';

interface GovernanceOverviewProps {
  onNavigate: (section: string) => void;
}

const alertPillColors: Record<string, string> = {
  critical: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  warning: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  informational: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
};

export default function GovernanceOverview({ onNavigate }: GovernanceOverviewProps) {
  const {
    data: overviewData,
    isLoading,
    error,
    refetch,
  } = useGmbGovernanceOverview();

  return (
    <GBMContentWrapper error={error} onRetry={() => refetch()} loading={isLoading}>
      <Inner onNavigate={onNavigate} overview={overviewData?.data || {}} />
    </GBMContentWrapper>
  );
}

function Inner({ onNavigate, overview }: { onNavigate: (section: string) => void; overview: any }) {
  const isVerified = overview.verification_status === 'verified' || overview.verification_status === 'active';
  const snapshot = overview.live_preview_snapshot || {};
  const severityCounts = overview.active_alerts_count_by_severity || { critical: 0, warning: 0, informational: 0 };

  const snapshots = [
    {
      id: 'live-preview',
      title: 'Live Preview',
      borderColor: 'blue',
      type: 'gmb-card',
      label: `Last synced ${snapshot.last_synced_label || '—'}`,
    },
    {
      id: 'activity-log',
      title: 'Activity Log',
      borderColor: 'green',
      type: 'log-rows',
      logRows: (overview.activity_log_summary || []).map((row: any) => ({
        action: row.action,
        actor: row.user,
        time: row.time,
      })),
      label: `${overview.changes_today || 0} changes today`,
    },
    {
      id: 'alerts-permissions',
      title: 'Alerts & Permissions',
      borderColor: 'red',
      type: 'alerts-pills',
      alertPills: (overview.alerts_summary || []).map((a: any) => ({
        text: a.title,
        color: a.severity === 'critical' ? 'red' : a.severity === 'warning' ? 'orange' : 'yellow',
      })),
      label: `${overview.active_alerts_count || 0} active alerts`,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <GBMStatCard
          label="Live Profile"
          value={overview.live_profile_status === 'active' ? 'Active' : 'Inactive'}
          change="Published"
          color="blue"
          icon="ri-global-line"
        />
        <GBMStatCard
          label="Active Alerts"
          value={overview.active_alerts_count ?? 0}
          change={overview.active_alerts_count === 1 ? 'Needs attention' : 'Need attention'}
          color="green"
          icon="ri-notification-3-line"
        />
        <GBMStatCard
          label="Google Account"
          value={overview.google_account_status === 'full_access' ? 'Full Access' : 'Unlinked'}
          change={overview.google_account_status === 'full_access' ? 'Connected' : 'Not connected'}
          color="purple"
          icon="ri-google-line"
        />
        <GBMStatCard
          label="Verification"
          value={isVerified ? 'Verified' : 'Not Verified'}
          change={isVerified ? 'Google confirmed ownership' : 'Verification required'}
          color={isVerified ? 'green' : 'red'}
          icon={isVerified ? 'ri-shield-check-line' : 'ri-error-warning-line'}
          action={
            !isVerified ? (
              <button
                className="w-full flex items-center justify-center gap-2 bg-white border border-red-200 hover:border-red-300 text-red-700 text-xs font-medium px-3 py-2 rounded-md transition-colors whitespace-nowrap"
                onClick={() => window.open('https://business.google.com/verify', '_blank')}
              >
                <i className="ri-shield-check-line"></i>
                Verify Now
              </button>
            ) : undefined
          }
        />
      </div>

      <TutorialCard
        title="How Governance Works"
        description="Monitor, audit and control your listing"
        duration="2:00 min"
        variant="dark"
      />

      <div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Governance Snapshot</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {snapshots.map((snap) => (
            <button
              key={snap.id}
              onClick={() => onNavigate(snap.id)}
              className={`text-left rounded-lg border-l-4 border-${snap.borderColor}-500 bg-white dark:bg-gray-800 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200 dark:border-gray-700`}
            >
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">{snap.title}</h4>

              {snap.type === 'gmb-card' && (
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3 mb-3 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{snapshot.business_name || 'Your Business'}</span>
                    {isVerified && <i className="ri-verified-badge-fill text-blue-500 text-sm"></i>}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mb-1.5">
                    <span className="font-bold text-gray-900 dark:text-white">{snapshot.rating || '0.0'}</span>
                    <i className="ri-star-fill text-yellow-400 text-xs"></i>
                    <span className="text-gray-400">({snapshot.review_count || 0})</span>
                    <span className="text-gray-400">{snapshot.category}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-green-600 mb-1.5">
                    <i className="ri-time-line text-xs"></i>
                    <span>{snapshot.is_open === null ? 'Open status unavailable' : snapshot.is_open ? 'Open now' : 'Closed'} · {snapshot.phone}</span>
                  </div>
                </div>
              )}

              {snap.type === 'log-rows' && (
                <div className="space-y-2 mb-3">
                  {(snap as any).logRows.map((row: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                      <span className="text-gray-700 dark:text-gray-300 truncate">{row.action}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{row.actor}</span>
                      <span className="text-gray-400 ml-auto whitespace-nowrap">{row.time}</span>
                    </div>
                  ))}
                </div>
              )}

              {snap.type === 'alerts-pills' && (
                <div className="space-y-2 mb-3">
                  <div className="flex flex-wrap gap-1.5">
                    {(snap as any).alertPills.map((pill: any, i: number) => (
                      <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${alertPillColors[pill.color] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                        {pill.text}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {snap.type === 'alerts-pills' && severityCounts.critical > 0 && (
                <div className="flex items-center gap-1.5 mb-2">
                  <GMBSeverityBadge severity="critical" />
                </div>
              )}

              <div className="text-xs text-gray-500 dark:text-gray-400">{snap.label}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}